import React from 'react';
import { Check, ChevronRight, Lock } from 'lucide-react';
import Tooltip from './Tooltip';

/**
 * ProcessMapValidationBar
 *
 * Single ordered control group for everything a reviewer does to a process map.
 *
 * Replaces: the "Process Map Pending Approval" banner + its green Approve button
 * (previously in SOPBuilder), and the scattered Run Verifier / Edit / Export /
 * Validate buttons that were spread across the toolbar row.
 *
 * The order is the product opinion: cheap + deterministic before expensive +
 * probabilistic, human edit in the middle, approval locks, export last.
 *
 *   1. Run Verifier          — deterministic, internal, fact-checks vs. source docs
 *   2. Run Challenger Model  — expensive, external, second-opinion on substance
 *   3. Edit                  — human reconciles what 1 and 2 flagged
 *   4. Approve Map           — locks the map, unlocks the next SOP Builder step
 *   5. Export                — artifacts, incl. the audit trail package
 *
 * Regenerate is deliberately NOT in this bar. It is destructive (it discards
 * manual edits) and belongs with the generation controls, not the review chain.
 */

export type ValidationStepStatus =
  | 'done'      // completed successfully
  | 'running'   // in flight
  | 'active'    // this is the next thing to do
  | 'pending'   // not yet reached, but reachable
  | 'blocked';  // prerequisite not met

export interface ValidationStepDef {
  /** stable key */
  id: string;
  /** display label */
  label: string;
  /** label to show while status === 'running' */
  runningLabel?: string;
  icon: React.ReactNode;
  status: ValidationStepStatus;
  /** hover copy explaining what this step does / why it is blocked */
  tooltip: string;
  onClick?: () => void;
  /** hard-disable regardless of status */
  disabled?: boolean;
  /** replaces the default button entirely (used for the Export dropdown) */
  render?: (args: { stepNumber: number; status: ValidationStepStatus }) => React.ReactNode;
  /** rendered immediately after the button inside the same segment (e.g. Save) */
  trailing?: React.ReactNode;
  /** green treatment for the approval step */
  tone?: 'default' | 'approve';
}

interface ProcessMapValidationBarProps {
  steps: ValidationStepDef[];
  /** shown at the far right of the bar, e.g. approval timestamp */
  statusNote?: React.ReactNode;
  className?: string;
}

const STATUS_CLASSES: Record<ValidationStepStatus, string> = {
  done: 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100',
  running: 'bg-blue-50 text-blue-700 border-blue-200 cursor-wait',
  active: 'bg-electric-blue text-white border-electric-blue hover:bg-darker-blue shadow-sm',
  pending: 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:text-gray-900',
  blocked: 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed',
};

// Green is reserved for "do this now" (active) and "done" — NOT for pending. A pending Approve
// step means Verifier/Challenger haven't been run yet; it must read as unearned (grey), same as
// every other step's pending state, or it falsely looks completed/approved before anything has
// actually happened. (Bug: this table used to include a green `pending` entry that overrode the
// correct grey from STATUS_CLASSES — see ProcessMapValidationBar usage in pages/ProcessMaps.tsx,
// the `approve` step's `tone: 'approve'`.) Omitting `pending` here lets the lookup at the call
// site fall through to STATUS_CLASSES.pending (grey), same as steps 1/2/3/5.
const APPROVE_STATUS_CLASSES: Partial<Record<ValidationStepStatus, string>> = {
  active: 'bg-green-600 text-white border-green-600 hover:bg-green-700 shadow-sm',
  done: 'bg-green-600 text-white border-green-600 hover:bg-green-700',
};

const BADGE_CLASSES: Record<ValidationStepStatus, string> = {
  done: 'bg-green-600 text-white',
  running: 'bg-blue-600 text-white',
  active: 'bg-white/25 text-white',
  pending: 'bg-gray-200 text-gray-600',
  blocked: 'bg-gray-200 text-gray-400',
};

const StepBadge: React.FC<{
  status: ValidationStepStatus;
  n: number;
  tone?: 'default' | 'approve';
  /** badge sits on the bar itself, not inside a filled button — needs its own contrast */
  standalone?: boolean;
}> = ({
  status,
  n,
  tone,
  standalone = false,
}) => {
  const base = 'w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-semibold flex-shrink-0';
  const cls =
    standalone && status === 'active'
      ? 'bg-electric-blue text-white'
      : tone === 'approve' && (status === 'active' || status === 'done')
        ? 'bg-white/25 text-white'
        : BADGE_CLASSES[status];

  if (status === 'done') {
    return (
      <span className={`${base} ${cls}`}>
        <Check className="w-2.5 h-2.5" strokeWidth={3} />
      </span>
    );
  }
  if (status === 'blocked') {
    return (
      <span className={`${base} ${cls}`}>
        <Lock className="w-2.5 h-2.5" strokeWidth={2.5} />
      </span>
    );
  }
  return <span className={`${base} ${cls}`}>{n}</span>;
};

const ProcessMapValidationBar: React.FC<ProcessMapValidationBarProps> = ({
  steps,
  statusNote,
  className = '',
}) => {
  if (!steps.length) return null;

  return (
    <div
      className={`flex items-center gap-1.5 bg-white border border-gray-200 rounded-xl px-2.5 py-1.5 shadow-sm flex-wrap ${className}`}
      role="group"
      aria-label="Process map validation steps"
    >
      <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 pr-1 select-none whitespace-nowrap">
        Validation
      </span>

      {steps.map((step, i) => {
        const stepNumber = i + 1;
        const isBlocked = step.status === 'blocked' || !!step.disabled;
        const toneClasses =
          step.tone === 'approve' && APPROVE_STATUS_CLASSES[step.status]
            ? APPROVE_STATUS_CLASSES[step.status]
            : STATUS_CLASSES[step.status];

        return (
          <React.Fragment key={step.id}>
            {i > 0 && <ChevronRight className="w-3 h-3 text-gray-300 flex-shrink-0" aria-hidden="true" />}

            <div className="flex items-center gap-1">
              {step.render ? (
                // Not wrapped in <Tooltip>: its portal span is an inline-block that
                // traps the dropdown panel this step renders. The dropdown carries
                // its own help panel, so the hover text falls back to a native title.
                <div className="flex items-center gap-1.5" title={step.tooltip}>
                  <StepBadge status={step.status} n={stepNumber} tone={step.tone} standalone />
                  {step.render({ stepNumber, status: step.status })}
                </div>
              ) : (
                // A disabled button dispatches no mouse events, so the hover never
                // reaches the wrapper and the tooltip saying *why* the step is
                // blocked — the only place that reason is written — never opens.
                <Tooltip text={step.tooltip} className={isBlocked ? 'cursor-not-allowed' : ''}>
                  <button
                    type="button"
                    onClick={isBlocked ? undefined : step.onClick}
                    disabled={isBlocked || step.status === 'running'}
                    aria-current={step.status === 'active' ? 'step' : undefined}
                    className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-all flex items-center gap-1.5 whitespace-nowrap disabled:cursor-not-allowed ${isBlocked ? 'pointer-events-none' : ''} ${toneClasses}`}
                  >
                    <StepBadge status={step.status} n={stepNumber} tone={step.tone} />
                    {step.status === 'running' ? (
                      <>
                        <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        <span>{step.runningLabel || `${step.label}...`}</span>
                      </>
                    ) : (
                      <>
                        <span className="flex-shrink-0">{step.icon}</span>
                        <span>{step.label}</span>
                      </>
                    )}
                  </button>
                </Tooltip>
              )}
              {step.trailing}
            </div>
          </React.Fragment>
        );
      })}

      {statusNote && (
        <span className="text-[11px] text-gray-500 pl-1.5 border-l border-gray-200 ml-1 whitespace-nowrap">
          {statusNote}
        </span>
      )}
    </div>
  );
};

export default ProcessMapValidationBar;
